/**
 * Utility functions for calculating delivery slots and earliest delivery dates
 * Prep hours are counted only during working hours
 */

export interface DeliverySlot {
  id: number;
  start_time: string;
  end_time: string;
}

export interface VendorTimings {
  id?: number;
  monday_open?: boolean;
  monday_opening_time?: string;
  monday_closing_time?: string;
  tuesday_open?: boolean;
  tuesday_opening_time?: string;
  tuesday_closing_time?: string;
  wednesday_open?: boolean;
  wednesday_opening_time?: string;
  wednesday_closing_time?: string;
  thursday_open?: boolean;
  thursday_opening_time?: string;
  thursday_closing_time?: string;
  friday_open?: boolean;
  friday_opening_time?: string;
  friday_closing_time?: string;
  saturday_open?: boolean;
  saturday_opening_time?: string;
  saturday_closing_time?: string;
  sunday_open?: boolean;
  sunday_opening_time?: string;
  sunday_closing_time?: string;
  has_special_hours?: boolean;
  special_hours_note?: string;
  [key: string]: unknown;
}

export interface DeliverySlotsData {
  other_days: DeliverySlot[];
  max_prep_hours: number;
}

export interface EarliestDeliveryResult {
  deliveryDate: Date;
  deliverySlots: DeliverySlot[];
}

interface WorkingHours {
  isOpen: boolean;
  openingTime: number | null; // milliseconds since start of day
  closingTime: number | null; // milliseconds since start of day
}

const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Converts a time string (HH:MM:SS or HH:MM) to a Date object (using epoch date for time-only comparison)
 */
const getDateFromTimeString = (timeString: string): Date => {
  return new Date(`1970-01-01T${timeString}Z`);
};

/**
 * Gets the day name from a Date object
 */
const getDayName = (date: Date): string => {
  const dayIndex = date.getDay();
  const dayName = dayNames[dayIndex];
  return dayName ?? dayNames[0] ?? "Sunday";
};

/**
 * Gets the time-of-day in milliseconds since start of day from a Date object
 */
const getTimeOfDay = (date: Date): number => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  return (hours * 60 * 60 + minutes * 60 + seconds) * 1000;
};

/**
 * Gets working hours for a specific day
 */
const getWorkingHours = (
  vendorTimings: VendorTimings,
  date: Date,
): WorkingHours => {
  const dayName = getDayName(date);
  const dayKey = dayName.toLowerCase();
  const isOpen =
    (vendorTimings[`${dayKey}_open` as keyof VendorTimings] as boolean) ??
    false;
  const openingTimeStr = vendorTimings[
    `${dayKey}_opening_time` as keyof VendorTimings
  ] as string | undefined;
  const closingTimeStr = vendorTimings[
    `${dayKey}_closing_time` as keyof VendorTimings
  ] as string | undefined;

  return {
    isOpen,
    openingTime: openingTimeStr
      ? getDateFromTimeString(openingTimeStr).getTime()
      : null,
    closingTime: closingTimeStr
      ? getDateFromTimeString(closingTimeStr).getTime()
      : null,
  };
};

/**
 * Calculates prep completion time considering only working hours
 * Prep starts from current time (or opening time if next day) and counts only during working hours
 * If shop is closed, prep continues to next day
 * @returns The date and time when prep will be completed, or null if not calculable
 */
export const calculatePrepCompletionTime = (
  vendorTimings: VendorTimings,
  maxPrepMilliseconds: number,
  currentDate: Date,
): { completionDate: Date; completionTimeOfDay: number } | null => {
  let remainingPrepTime = maxPrepMilliseconds;
  let currentCheckDate = new Date(currentDate);
  const maxDaysToCheck = 14; // Check up to 2 weeks ahead
  let daysChecked = 0;

  // Get current time of day
  let currentTimeOfDay = getTimeOfDay(currentDate);

  // If prep time is 0, we still want to find the earliest valid delivery time (now or next opening)
  if (remainingPrepTime <= 0) {
    remainingPrepTime = 1; // Treat as 1ms to enter the loop and find the next valid time
  }

  while (remainingPrepTime > 0 && daysChecked < maxDaysToCheck) {
    const workingHours = getWorkingHours(vendorTimings, currentCheckDate);

    // If shop is closed, move to next day
    if (
      !workingHours.isOpen ||
      !workingHours.openingTime ||
      !workingHours.closingTime
    ) {
      currentCheckDate = new Date(currentCheckDate);
      currentCheckDate.setDate(currentCheckDate.getDate() + 1);
      currentCheckDate.setHours(0, 0, 0, 0);
      currentTimeOfDay = 0;
      daysChecked++;
      continue;
    }

    // Determine start time for this day
    let dayStartTime = currentTimeOfDay;
    if (dayStartTime < workingHours.openingTime) {
      dayStartTime = workingHours.openingTime;
    }

    // Calculate available working time for this day
    const availableTime = workingHours.closingTime - dayStartTime;

    if (availableTime <= 0) {
      // No time available today, move to next day
      currentCheckDate = new Date(currentCheckDate);
      currentCheckDate.setDate(currentCheckDate.getDate() + 1);
      currentCheckDate.setHours(0, 0, 0, 0);
      currentTimeOfDay = 0;
      daysChecked++;
      continue;
    }

    if (remainingPrepTime <= availableTime) {
      // Prep completes within this day
      const completionTimeOfDay = dayStartTime + remainingPrepTime;
      return {
        completionDate: new Date(currentCheckDate),
        completionTimeOfDay,
      };
    } else {
      // Prep continues to next day
      remainingPrepTime -= availableTime;
      currentCheckDate = new Date(currentCheckDate);
      currentCheckDate.setDate(currentCheckDate.getDate() + 1);
      currentCheckDate.setHours(0, 0, 0, 0);
      currentTimeOfDay = 0;
      daysChecked++;
    }
  }

  return null; // Could not calculate within reasonable time
};

/**
 * Finds available slots based on prep completion time
 * @param slots - Available delivery slots for the day
 * @param prepCompletionTimeOfDay - Time of day when prep completes (milliseconds since start of day)
 * @param workingHours - Working hours for the day
 * @returns Array of available slots (the slot containing prep completion and the next one)
 */
const findAvailableSlots = (
  slots: DeliverySlot[],
  prepCompletionTimeOfDay: number,
  workingHours: WorkingHours,
): DeliverySlot[] => {
  if (slots.length === 0) {
    return [];
  }

  const firstSlot = slots[0];
  const lastSlot = slots[slots.length - 1];

  if (!firstSlot || !lastSlot) {
    return [];
  }

  const firstSlotStart = getDateFromTimeString(firstSlot.start_time).getTime();
  const lastSlotEnd = getDateFromTimeString(lastSlot.end_time).getTime();

  // If prep finishes before the first slot, return empty (will go to next day)
  if (prepCompletionTimeOfDay < firstSlotStart) {
    return [];
  }

  // If prep finishes after the last slot, return empty (will go to next day)
  if (prepCompletionTimeOfDay > lastSlotEnd) {
    return [];
  }

  // Find the slot that contains prep completion time or the next slot
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot) {
      continue;
    }

    const slotStart = getDateFromTimeString(slot.start_time).getTime();
    const slotEnd = getDateFromTimeString(slot.end_time).getTime();

    // Check if prep completion time is within this slot
    if (
      prepCompletionTimeOfDay >= slotStart &&
      prepCompletionTimeOfDay < slotEnd
    ) {
      // Prep finishes within this slot, return this slot and the next one
      const availableSlots: DeliverySlot[] = [slot];
      if (i + 1 < slots.length) {
        const nextSlot = slots[i + 1];
        if (nextSlot) {
          const nextSlotStart = getDateFromTimeString(
            nextSlot.start_time,
          ).getTime();
          // Only include next slot if it's within working hours
          if (
            workingHours.closingTime !== null &&
            nextSlotStart < workingHours.closingTime
          ) {
            availableSlots.push(nextSlot);
          }
        }
      }
      return availableSlots;
    }

    // Check if prep completion time is exactly at the end of this slot
    if (prepCompletionTimeOfDay === slotEnd) {
      // Return the next slot
      if (i + 1 < slots.length) {
        const nextSlot = slots[i + 1];
        if (nextSlot) {
          const nextSlotStart = getDateFromTimeString(
            nextSlot.start_time,
          ).getTime();
          // Only include if it's within working hours
          if (
            workingHours.closingTime !== null &&
            nextSlotStart < workingHours.closingTime
          ) {
            return [nextSlot];
          }
        }
      }
      // No next slot available, return empty (will go to next day)
      return [];
    }
  }

  return [];
};

/**
 * Gets the earliest delivery date and available slots
 * @param vendorTimings - Vendor timing information
 * @param deliverySlotsData - Available delivery slots data
 * @param currentDate - Current date (defaults to now)
 * @returns Earliest delivery result with date and available slots, or null if not available
 */
export const getEarliestDeliveryForToday = (
  vendorTimings: VendorTimings | null | undefined,
  deliverySlotsData: DeliverySlotsData | null | undefined,
  currentDate: Date = new Date(),
): EarliestDeliveryResult | null => {
  if (!vendorTimings || !deliverySlotsData) {
    return null;
  }

  const maxPrepMinutes = Number(deliverySlotsData.max_prep_hours || 0) * 60;
  const maxPrepMilliseconds = maxPrepMinutes * 60 * 1000;

  // Calculate prep completion time considering only working hours
  const prepResult = calculatePrepCompletionTime(
    vendorTimings,
    maxPrepMilliseconds,
    currentDate,
  );

  if (!prepResult) {
    return null;
  }

  const { completionDate, completionTimeOfDay } = prepResult;

  // Check if completion is today
  const isToday =
    completionDate.getDate() === currentDate.getDate() &&
    completionDate.getMonth() === currentDate.getMonth() &&
    completionDate.getFullYear() === currentDate.getFullYear();

  if (!isToday) {
    return null;
  }

  // Get slots for the day
  const slots = deliverySlotsData.other_days || [];
  if (slots.length === 0) {
    return null;
  }

  // Get working hours for completion day
  const workingHours = getWorkingHours(vendorTimings, completionDate);
  if (!workingHours.isOpen) {
    return null;
  }

  // Find available slots based on prep completion time
  const availableSlots = findAvailableSlots(
    slots,
    completionTimeOfDay,
    workingHours,
  );

  if (availableSlots.length === 0) {
    return null;
  }

  const deliveryDate = new Date(completionDate);
  deliveryDate.setHours(0, 0, 0, 0);

  return {
    deliveryDate,
    deliverySlots: availableSlots,
  };
};

/**
 * Gets the earliest delivery date and available slots considering all factors
 * @param vendorTimings - Vendor timing information
 * @param deliverySlotsData - Available delivery slots data
 * @param currentDate - Current date (defaults to now)
 * @returns Earliest delivery result with date and available slots, or null if not available
 */
export const getEarliestDeliveryDate = (
  vendorTimings: VendorTimings | null | undefined,
  deliverySlotsData: DeliverySlotsData | null | undefined,
  currentDate: Date = new Date(),
): EarliestDeliveryResult | null => {
  if (!vendorTimings || !deliverySlotsData) {
    return null;
  }

  // First, check if we can deliver today
  const todayResult = getEarliestDeliveryForToday(
    vendorTimings,
    deliverySlotsData,
    currentDate,
  );

  if (todayResult) {
    return todayResult;
  }

  // If not today, calculate prep completion and find next available day with slots
  const maxPrepMinutes = Number(deliverySlotsData.max_prep_hours || 0) * 60;
  const maxPrepMilliseconds = maxPrepMinutes * 60 * 1000;

  // Calculate prep completion time
  const prepResult = calculatePrepCompletionTime(
    vendorTimings,
    maxPrepMilliseconds,
    currentDate,
  );

  if (!prepResult) {
    return null;
  }

  const { completionDate, completionTimeOfDay } = prepResult;
  const slots = deliverySlotsData.other_days || [];

  if (slots.length === 0) {
    return null;
  }

  const firstSlot = slots[0];
  const lastSlot = slots[slots.length - 1];

  if (!firstSlot || !lastSlot) {
    return null;
  }

  const firstSlotStart = getDateFromTimeString(firstSlot.start_time).getTime();
  const lastSlotEnd = getDateFromTimeString(lastSlot.end_time).getTime();

  // Check completion day
  let checkDate = new Date(completionDate);
  const maxDaysToCheck = 14;
  let daysChecked = 0;

  while (daysChecked < maxDaysToCheck) {
    checkDate.setHours(0, 0, 0, 0);
    const workingHours = getWorkingHours(vendorTimings, checkDate);

    if (
      !workingHours.isOpen ||
      !workingHours.openingTime ||
      !workingHours.closingTime
    ) {
      // Shop is closed, move to next day
      checkDate.setDate(checkDate.getDate() + 1);
      daysChecked++;
      continue;
    }

    const isCompletionDay =
      checkDate.getDate() === completionDate.getDate() &&
      checkDate.getMonth() === completionDate.getMonth() &&
      checkDate.getFullYear() === completionDate.getFullYear();

    if (isCompletionDay) {
      // On completion day, check if prep finishes before first slot or after last slot
      if (
        completionTimeOfDay < firstSlotStart ||
        completionTimeOfDay > lastSlotEnd
      ) {
        // Prep finishes before first slot or after last slot, go to next day's first slot
        checkDate.setDate(checkDate.getDate() + 1);
        daysChecked++;
        continue;
      }

      // Try to find slots for completion day
      const availableSlots = findAvailableSlots(
        slots,
        completionTimeOfDay,
        workingHours,
      );

      if (availableSlots.length > 0) {
        return {
          deliveryDate: new Date(checkDate),
          deliverySlots: availableSlots,
        };
      }

      // No slots available for completion day, go to next day
      checkDate.setDate(checkDate.getDate() + 1);
      daysChecked++;
      continue;
    } else {
      // For days after completion, use first slot and next slot if available
      // Check if first slot is within working hours
      if (
        workingHours.openingTime !== null &&
        firstSlotStart >= workingHours.openingTime &&
        workingHours.closingTime !== null &&
        firstSlotStart < workingHours.closingTime
      ) {
        const availableSlots: DeliverySlot[] = [firstSlot];
        // Include the next slot if available
        if (slots.length > 1) {
          const nextSlot = slots[1];
          if (nextSlot) {
            const nextSlotStart = getDateFromTimeString(
              nextSlot.start_time,
            ).getTime();
            if (
              workingHours.closingTime !== null &&
              nextSlotStart < workingHours.closingTime
            ) {
              availableSlots.push(nextSlot);
            }
          }
        }
        return {
          deliveryDate: new Date(checkDate),
          deliverySlots: availableSlots,
        };
      }

      // First slot not available, move to next day
      checkDate.setDate(checkDate.getDate() + 1);
      daysChecked++;
    }
  }

  return null;
};

/**
 * Simple estimation of earliest delivery date based only on prep time and working hours
 * (Used for vendor-managed delivery where specific time slots aren't required)
 */
export const getEarliestDeliveryDateOnly = (
  vendorTimings: VendorTimings,
  maxPrepHours: number,
  currentDate: Date,
): Date | null => {
  const prepMilliseconds = maxPrepHours * 60 * 60 * 1000;
  const result = calculatePrepCompletionTime(
    vendorTimings,
    prepMilliseconds,
    currentDate,
  );

  if (!result) {
    return null;
  }

  const finalDate = new Date(result.completionDate);
  finalDate.setHours(0, 0, 0, 0);
  finalDate.setMilliseconds(result.completionTimeOfDay);
  return finalDate;
};
